import React, { useEffect, useState, useRef } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Container, Button } from '../Layout';
import { gridContainerStyle, columnStyle } from '../CreateDeviceContent/styles';
import { Divider } from '@mui/material';
import { CircularProgress } from '@mui/material';
import i18n from '../../helpers/i18n';
import { getEmployees, getDevice, getLocationsAndCnpj, editDevice, getCurrentCompanyTotalActiveEmployees } from '../CreateDeviceContent/CreateDeviceContent.queries';
import { useFormik } from 'formik';
import { validationSchema } from '../CreateDeviceContent/validationSchema';
import { RestrictionSection, AuthorizedCollaboratorsSection, ControllerSection, DeviceSection, ButtonDisplaySection } from '../CreateEditSections/';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { deviceTypes, filterTypes } from '../../helpers/consts/deviceConstants';
import { useNavigate } from 'react-router-dom';
import { useError } from '../SnackbarError/ErrorContext';
import DeviceUpdatedAlert from '../DeviceUpdatedAlert/DeviceUpdatedAlert';
import { usePermissions } from '../../hooks/usePermissions';

const EditDeviceContent = ({ onDeviceNameFetched }) => {
    const queryClient = useQueryClient();

    const { id } = useParams();
    const navigate = useNavigate();

    const [offset, setOffset] = useState(0);
    const [byName, setByName] = useState('');
    const [employees, setEmployees] = useState([]);

    const [locations, setLocations] = useState([]);
    const [cnpjs, setCnpjs] = useState([]);

    const { showError } = useError();

    const [isOpenUpdatedAlert, setIsOpenUpdatedAlert] = useState(false);
    const [isCnpjUpdated, setIsCnpjUpdated] = useState(false);

    const { data: dataDevice } = useQuery({
        queryKey: [id],
        queryFn: () => getDevice({ identity: id }),
        retry: false,
        refetchOnWindowFocus: false
    });

        
    const { data: totalEmployeesData } = useQuery({
        queryKey: ['totalEmployees'],
        queryFn: getCurrentCompanyTotalActiveEmployees,
        refetchOnWindowFocus: false,
        retry: false
    });

    const totalEmployees = totalEmployeesData;
    
    const { data: dataEmployees, isLoading: isLoadingEmployees } = useQuery({
        queryKey: ['funcionarios', offset, byName, totalEmployees],
        queryFn: () => getEmployees({ limit: 50, offset, byName, totalEmployees }),
        refetchOnWindowFocus: false,
        keepPreviousData: true,
        retry: false,
        enabled: !!totalEmployees
    });

    const { data: dataLocationsAndCnpj, isLoading: isLoadingLocationsAndCnpjs } = useQuery({
        queryKey: ['locations'],
        queryFn: getLocationsAndCnpj,
        refetchOnWindowFocus: false,
        retry: false
    });

    const { permissions } = usePermissions();

    const [duplicateNameError, setDuplicateNameError] = useState('');

    const formik = useFormik({
        initialValues: {
            identity: '',
            deviceType: '',
            cnpj: '',
            name: '',
            company: '',
            timezone: '',
            activationKey: '',
            active: true,
            expirationDate: false,
            startPeriod: null,
            endPeriod: null,
            deleted: false,

            collaboratorsType: 'by_employee',
            employees: [],
            locations: [],

            //Crontroller
            enable_password_punch: false, //Ativar batida por matrícula e senha
            break_device: false, //Ativar dispositivo de pausa NR17
            enable_auto_update: false, //Ativar atualização automática (Tablet Ahgora)
            default_computer: false, //Ativar batida online padrão por link
            timezone_filter: false,  //Ativar fuso horário como filtro              
            triple_validation: false, //Ativar validação tripla para múltiplos usuários
            enable_punches_logs: false, //ativar logs de batidas
            enable_punch_backup: false, //Ativar download de arquivo com batida de ponto (Solicitar donwload do arquivo)
            enable_punch_by_manager: false, // Habilita que dispositivos Multi tenham a opção de registro de ponto pelo gestor
            blink: false,
            smile: false,
            head_movement: false,
            enable_facial_recognition_flow: false, //Ativação Reconhecimento Facial

            //Restriction
            online_only: false, //Com o dispositivo conectado à internet
            auto_timezone: false, //Com data, hora e fuso automáticos
            only_with_location: false, //Com geolocalização no dispositivo
            only_inside_geofence: false, //Somente dentro do posto de trabalho
            range_ip: false,
            ips: [], //Ip Range '123.123.123.23, 123.123.123.24'
            useSelectAllEmployees: false,

            //ButtonDisplay - Flags para ocultar botões no app Multi (por padrão vem false = exibir)
            hide_password_config_access: false,
            hide_reconfigure_multi: false,
            hide_punch_receipt_display_time: false,
        },
        validationSchema,
        onSubmit: values => {
            if (values.employees.length === 0 && values.locations.length === 0) {
                showError(i18n.t('pages.errors.device.noEmployeeSelected'));
                return;
            }
            
            // Limpa o erro de nome duplicado ao tentar submeter novamente
            setDuplicateNameError('');
            
            mutationEditDevice.mutate({ identity: id, deviceData: values, queryClient });
        }
    });

    const prevDataDeviceRef = useRef();
    useEffect(() => {
        if (dataDevice && prevDataDeviceRef.current !== dataDevice) {
            prevDataDeviceRef.current = dataDevice;

            onDeviceNameFetched(dataDevice.name);

            formik.setValues({
                ...formik.values,
                identity: dataDevice.identity,
                company: dataDevice.company,
                deviceType: dataDevice.device_type || '',
                cnpj: dataDevice.cnpj || '',
                name: dataDevice.name || '',
                timezone: dataDevice.timezone || '',
                activationKey: dataDevice.activationKey || '',
                active: dataDevice.active ?? true,
                expirationDate: dataDevice.startPeriod ? true : false,
                startPeriod: dataDevice.startPeriod ? dayjs.unix(dataDevice.startPeriod).add(1, 'day') : null,
                endPeriod: dataDevice.endPeriod ? dayjs.unix(dataDevice.endPeriod) : null,
                collaboratorsType: dataDevice.locations?.length > 0 ? 'by_location' : 'by_employee',
                employees: dataDevice.employees || [],
                locations: dataDevice.locations || [],
                deleted: dataDevice.deleted ?? false,

                // Controller
                enable_password_punch: dataDevice.enable_password_punch ?? false,
                break_device: dataDevice.break_device ?? false,
                is_repa: dataDevice.is_repa ?? false,
                enable_auto_update: dataDevice.enable_auto_update ?? false,
                default_computer: dataDevice.default_computer ?? false,
                timezone_filter: dataDevice.timezone_filter ?? false,
                triple_validation: dataDevice.triple_validation ?? false,
                enable_punches_logs: dataDevice.enable_punches_logs ?? false,
                enable_punch_backup: dataDevice.punches_backup?.enable_punch_backup ?? false,
                enable_punch_by_manager: dataDevice.enable_punch_by_manager ?? false,
                save_photo_on_punch: dataDevice.save_photo_on_punch ?? false,
                blink: dataDevice.blink ?? false,
                smile: dataDevice.smile ?? false,
                head_movement: dataDevice.head_movement ?? false,
                enable_facial_recognition_flow: dataDevice.enable_facial_recognition_flow ?? false,

                // Restriction
                online_only: dataDevice.online_only ?? false,
                auto_timezone: dataDevice.auto_timezone ?? false,
                only_with_location: dataDevice.only_with_location ?? false,
                only_inside_geofence: dataDevice.only_inside_geofence ?? false,
                range_ip: dataDevice.range_ip ? true : false,
                ips: dataDevice.range_ip ? dataDevice.range_ip.split(',') : [],

                // ButtonDisplay
                hide_password_config_access: dataDevice.hide_password_config_access ?? false,
                hide_reconfigure_multi: dataDevice.hide_reconfigure_multi ?? false,
                hide_punch_receipt_display_time: dataDevice.hide_punch_receipt_display_time ?? false,
            });
        }
    }, [dataDevice, formik, onDeviceNameFetched]);

    useEffect(() => {
        if (dataLocationsAndCnpj?.locations) {
            let locations = dataLocationsAndCnpj.locations;

            if (typeof locations === 'object' && !Array.isArray(locations)) {
                locations = Object.values(locations);
            }

            const locationArray = locations.map(location => ({ value: location, title: location }));
            setLocations(locationArray);
            setCnpjs(dataLocationsAndCnpj.cnpjs.map(cnpjItem => ({ 
                value: cnpjItem.cnpj, 
                title: `${cnpjItem.cnpj} - ${cnpjItem.razao_social}` 
            })));
        }
    }, [dataLocationsAndCnpj]);

    useEffect(() => {
        // Apenas seta um CNPJ padrão se ainda não houver nenhum valor
        if (cnpjs.length > 0 && !formik.values.cnpj && !isCnpjUpdated && dataDevice) {
            formik.setFieldValue('cnpj', cnpjs[0].value);
            setIsCnpjUpdated(true);
        }
    }, [cnpjs, formik.values.cnpj, dataDevice, isCnpjUpdated, formik]);

    useEffect(() => {
        if (dataEmployees?.data) {
            const newEmployees = dataEmployees.data.map(employee => ({
                value: employee.dados.matricula,
                title: `${employee.dados.matricula} | ${employee.dados.nome}`
            }));

            setEmployees(prevEmployees => {
                const uniqueEmployees = newEmployees.filter(newEmployee =>
                    !prevEmployees.some(existingEmployee => existingEmployee.value === newEmployee.value)
                );
                return [...prevEmployees, ...uniqueEmployees];
            });
        }
    }, [dataEmployees, byName]);

    const mutationEditDevice = useMutation({
        mutationFn: editDevice,
        onSuccess: (data) => {
            if (!data.response.result && data.response.errorType) {
                let errorMsg = i18n.t('pages.errors.device.createDefault');

                // Tratamento de erros específicos que vem do pw retornando 200 como status
                switch (data.response.errorType) {
                    case 'existsName':
                        setDuplicateNameError(i18n.t('pages.errors.device.existsName'));
                        showError(i18n.t('pages.errors.device.existsNameSnackbar'));
                        return;
                    case 'emptyLocation':
                        errorMsg = i18n.t('pages.errors.device.emptyLocation');
                        break;
                    default:
                        errorMsg = i18n.t('pages.errors.device.createWithType', { errorType: data.response.errorType });
                }

                showError(errorMsg);
                return;
            }

            setIsOpenUpdatedAlert(true);
        },
        onError: (e) => {
            showError(i18n.t('pages.errors.device.createDefault'));
        },
    });

    const handleScroll = (event) => {
        if (isLoadingEmployees || byName) return;
        const { scrollHeight, scrollTop, clientHeight } = event.target;
        if (scrollTop + clientHeight >= scrollHeight - 5) {
            setOffset(prevOffset => prevOffset + 1);
        }
    };

    const handleFilterSearch = (searchText) => {
        setByName(searchText); 
    };

    function handleEditedDevice() {
        setIsOpenUpdatedAlert(false);
        navigate('/');
    }

    if (!dataDevice) {
        return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', paddingTop: '100px' }}>
            <CircularProgress />
        </div>;
    }

    return (
        <>
            <DeviceUpdatedAlert isOpen={isOpenUpdatedAlert} onClose={handleEditedDevice} />
            <Container>
                <form style={{ width: '100%' }} onSubmit={formik.handleSubmit}>
                    <div style={gridContainerStyle}>
                        <div style={columnStyle}>
                            <DeviceSection
                                formik={formik}
                                cnpjs={cnpjs}
                                deviceTypes={deviceTypes}
                                isLoadingLocationsAndCnpjs={isLoadingLocationsAndCnpjs}
                                variant='edit'
                                deviceArchived={dataDevice?.deleted || false}
                                duplicateNameError={duplicateNameError}
                                onNameChange={() => setDuplicateNameError('')} 
                            />

                            <AuthorizedCollaboratorsSection
                                formik={formik}
                                employees={employees}
                                locations={locations}
                                isLoadingLocationsAndCnpjs={isLoadingLocationsAndCnpjs}
                                handleFilterSearch={handleFilterSearch}
                                handleScroll={handleScroll}
                                isLoadingEmployees={isLoadingEmployees}
                                filterTypes={filterTypes}
                                deviceArchived={dataDevice?.deleted || false}
                            />
                        </div>

                        <div style={columnStyle}>
                            <ControllerSection
                                formik={formik}
                                variant='edit'
                                permissions={permissions}
                                deviceArchived={dataDevice?.deleted || false}
                            />

                            {permissions?.permite_restricao_batida && (
                                <RestrictionSection
                                    formik={formik}
                                    deviceArchived={dataDevice?.deleted || false}
                                    permissions={permissions}
                                />
                            )}

                            <ButtonDisplaySection 
                                formik={formik} 
                                deviceArchived={dataDevice?.deleted || false}
                                permissions={permissions}
                            />
                        </div>
                    </div>
                    <Divider style={{ marginBottom: 16 }} />
                    <Button type="outlined" onClick={(e) => {
                        e.preventDefault();
                        navigate('/');
                    }}>{i18n.t('pages.newDevice.cancel')}</Button>
                    <Button type="submit" disabled={dataDevice?.deleted || false}>{i18n.t('pages.newDevice.editDevice')}</Button>
                </form>
            </Container>
        </>
    );
};

export default EditDeviceContent;
